import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Paperclip, FileText, Eye, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

/**
 * Bouton d'attachement d'un PDF/image à un projet avec classement auto dans Documents.
 *
 * Props:
 *  - projectId (string)         : projet auquel rattacher le fichier
 *  - folder (string)            : dossier Documents (ex: 'compromis', 'acte_vente')
 *  - subfolder (string, opt)    : sous-dossier
 *  - label (string)             : libellé du bouton
 *  - fileId (string, opt)       : id du fichier déjà attaché (affichera un bouton "Voir" + "Remplacer")
 *  - onAttached (fn)            : callback(fileId) appelé après upload
 *  - size (string)              : 'sm' | 'xs'
 *  - variant (string)           : style Shadcn
 *  - accept (string)            : mime types acceptés (default application/pdf,image/*)
 */
export const AttachDocumentButton = ({
  projectId,
  folder,
  subfolder = '',
  label = 'Joindre',
  fileId = null,
  onAttached,
  size = 'sm',
  variant = 'outline',
  accept = 'application/pdf,image/*',
  testId,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!projectId) {
      toast.error("Aucun projet lié — impossible d'attacher le document");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.set('file', file);
      const params = new URLSearchParams();
      params.set('project_id', projectId);
      params.set('folder', folder);
      if (subfolder) params.set('subfolder', subfolder);
      const res = await axios.post(`${API}/upload?${params.toString()}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${label} enregistré dans Documents`);
      onAttached?.(res.data.id, res.data);
    } catch (err) {
      console.error(err);
      toast.error('Échec de l\'upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleView = () => {
    if (!fileId) return;
    const url = `${API}/files/${fileId}?download=1`;
    window.open(url, '_blank');
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFile}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {fileId ? (
        <div className="inline-flex items-center gap-1">
          <Button
            size={size}
            variant={variant}
            className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-500/10 h-7 text-xs"
            onClick={handleView}
            data-testid={testId ? `${testId}-view` : undefined}
            title="Voir le document attaché"
          >
            <Eye className="w-3 h-3 mr-1" /> Voir {label.toLowerCase()}
          </Button>
          <Button
            size={size}
            variant="ghost"
            className="text-zinc-500 hover:text-zinc-300 h-7 text-xs px-1.5"
            onClick={handleSelect}
            disabled={uploading}
            title="Remplacer le document"
            data-testid={testId ? `${testId}-replace` : undefined}
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
          </Button>
        </div>
      ) : (
        <Button
          size={size}
          variant={variant}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 text-xs"
          onClick={handleSelect}
          disabled={uploading || !projectId}
          data-testid={testId}
          title={projectId ? `Uploader un ${label.toLowerCase()}` : 'Aucun projet associé'}
        >
          {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
          {label}
        </Button>
      )}
    </>
  );
};

export default AttachDocumentButton;
